import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_JULIA_C } from './fractalPresets';
import { EXPLORER_PALETTE, samplePalette, withPaletteTheme } from './fractalPalette';
import { findJuliaAttractingCycle, iterateUntilEscape } from './fractalIteration';
import { drawJuliaFractalWebGL } from './fractalWebGL';
import { renderFractalAnimationFrame } from './JuliaAnimationExplorers';

export const PARAMETER_PRESETS = Object.freeze([
  { id: 'dragon', label: 'Dragon', c: [-0.8, 0.156] },
  { id: 'rabbit', label: 'Rabbit', c: [-0.122561, 0.744862] },
  { id: 'basilica', label: 'Basilica', c: [-1, 0] },
  { id: 'siegel', label: 'Siegel', c: [-0.390541, 0.586788] },
  { id: 'dendrite', label: 'Dendrite', c: [0, 1] },
  { id: 'dust', label: 'Fatou Dust', c: [-0.76, 0.26] },
]);

const MANDELBROT_X_RANGE = Object.freeze([-2.1, 0.7]);
const MANDELBROT_Y_RANGE = Object.freeze([-1.4, 1.4]);
const JULIA_X_RANGE = Object.freeze([-1.65, 1.65]);
const JULIA_Y_RANGE = Object.freeze([-1.65, 1.65]);
const CANVAS_SIZE = 480;

function renderSmallFractal(canvas, xRange, yRange, palette) {
  if (!canvas) return;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !canvas.getContext?.mock) {
    return;
  }
  const juliaC = palette?.juliaC ?? null;
  const isJulia = Array.isArray(juliaC);
  const center = [(xRange[0] + xRange[1]) * 0.5, (yRange[0] + yRange[1]) * 0.5];
  const spanX = xRange[1] - xRange[0];
  const spanY = yRange[1] - yRange[0];
  if (
    renderFractalAnimationFrame(canvas, {
      center,
      basisX: [spanX, 0],
      basisY: [0, spanY],
      juliaC,
      isJulia,
      gScale: palette?.gScale ?? 7,
      phaseOffset: palette?.phaseOffset ?? 0,
      demStrength: isJulia ? 0.16 : 0.12,
      paletteTheme: palette?.theme || 'amethyst',
    })
  ) {
    return;
  }
  if (drawJuliaFractalWebGL(canvas, xRange, [yRange[1], yRange[0]], palette, { zoomTransition: false })) {
    return;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx || typeof ctx.createImageData !== 'function') return;
  const w = canvas.width;
  const h = canvas.height;
  const imageData = ctx.createImageData(w, h);
  if (!imageData?.data) return;
  const data32 = new Uint32Array(imageData.data.buffer);
  const xScale = (xRange[1] - xRange[0]) / w;
  const yScale = (yRange[1] - yRange[0]) / h;
  const iterResult = { iterations: 0, mu: 0, escaped: false };
  const rgba = [0, 0, 0, 0];
  for (let row = 0; row < h; row++) {
    const y0 = yRange[1] - (row + 0.5) * yScale;
    const rowOffset = row * w;
    for (let col = 0; col < w; col++) {
      const x0 = xRange[0] + (col + 0.5) * xScale;
      iterateUntilEscape(x0, y0, iterResult, 256, true, juliaC);
      if (iterResult.escaped) {
        samplePalette(iterResult.mu, palette, rgba);
        data32[rowOffset + col] =
          rgba[0] | (rgba[1] << 8) | (rgba[2] << 16) | (rgba[3] << 24);
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);
}

export function classifyParameterC([cx, cy]) {
  const cycle = findJuliaAttractingCycle([cx, cy]);
  if (cycle) {
    return {
      regime: 'connected-basin',
      label: `Connected Julia set (period-${cycle.period} attracting basin)`,
      period: cycle.period,
      multiplier: cycle.multiplier,
    };
  }
  const probe = iterateUntilEscape(cx, cy, {}, 512, true, null);
  if (!probe.escaped) {
    return {
      regime: 'connected-boundary',
      label: 'Connected Julia set (parabolic or Siegel boundary)',
      period: null,
      multiplier: null,
    };
  }
  return {
    regime: 'fatou-dust',
    label: `Disconnected Fatou Cantor dust (escapes in ${probe.iterations} steps)`,
    period: null,
    multiplier: null,
  };
}

export function cardioidParameterAtAngle(theta, radius = 0.95) {
  const cos1 = Math.cos(theta);
  const sin1 = Math.sin(theta);
  const cos2 = Math.cos(2 * theta);
  const sin2 = Math.sin(2 * theta);
  const cx = 0.5 * radius * cos1 - 0.25 * radius * radius * cos2;
  const cy = 0.5 * radius * sin1 - 0.25 * radius * radius * sin2;
  return [cx, cy];
}

export function JuliaParameterExplorer({ paletteTheme, onApplyParameter }) {
  const [juliaC, setJuliaC] = useState(() => [...DEFAULT_JULIA_C]);
  const [dragging, setDragging] = useState(false);
  const [morphing, setMorphing] = useState(false);
  const morphAngleRef = useRef(1.9);
  const mandelbrotCanvasRef = useRef(null);
  const juliaCanvasRef = useRef(null);

  const mandelbrotPalette = useMemo(
    () => withPaletteTheme({ ...EXPLORER_PALETTE, gScale: 7, theme: paletteTheme || 'amethyst' }, paletteTheme),
    [paletteTheme]
  );
  const juliaPalette = useMemo(
    () => withPaletteTheme({ ...EXPLORER_PALETTE, gScale: 7, juliaC, theme: paletteTheme || 'amethyst' }, paletteTheme),
    [paletteTheme, juliaC]
  );
  const classification = useMemo(() => classifyParameterC(juliaC), [juliaC]);

  useEffect(() => {
    renderSmallFractal(
      mandelbrotCanvasRef.current,
      MANDELBROT_X_RANGE,
      MANDELBROT_Y_RANGE,
      mandelbrotPalette
    );
  }, [mandelbrotPalette]);

  useEffect(() => {
    renderSmallFractal(
      juliaCanvasRef.current,
      JULIA_X_RANGE,
      JULIA_Y_RANGE,
      juliaPalette
    );
  }, [juliaPalette]);

  useEffect(() => {
    if (!morphing) return undefined;
    let frameId = null;
    let lastTime = null;
    const tick = timestamp => {
      const dt = lastTime === null ? 16 : Math.min(50, timestamp - lastTime);
      lastTime = timestamp;
      morphAngleRef.current = (morphAngleRef.current + (dt / 1000) * 0.9) % (2 * Math.PI);
      setJuliaC(cardioidParameterAtAngle(morphAngleRef.current));
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [morphing]);

  const updateFromClientPoint = useCallback((clientX, clientY, targetEl) => {
    const rect = targetEl?.getBoundingClientRect?.();
    if (!rect || !(rect.width > 0 && rect.height > 0)) return;
    const u = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    const cx = Number((MANDELBROT_X_RANGE[0] + u * (MANDELBROT_X_RANGE[1] - MANDELBROT_X_RANGE[0])).toFixed(4));
    const cy = Number((MANDELBROT_Y_RANGE[1] - v * (MANDELBROT_Y_RANGE[1] - MANDELBROT_Y_RANGE[0])).toFixed(4));
    setMorphing(false);
    setJuliaC([cx, cy]);
  }, []);

  const markerLeft = ((juliaC[0] - MANDELBROT_X_RANGE[0]) / (MANDELBROT_X_RANGE[1] - MANDELBROT_X_RANGE[0])) * 100;
  const markerTop = ((MANDELBROT_Y_RANGE[1] - juliaC[1]) / (MANDELBROT_Y_RANGE[1] - MANDELBROT_Y_RANGE[0])) * 100;

  const formatSign = value => (value >= 0 ? `+ ${value.toFixed(4)}i` : `\u2212 ${Math.abs(value).toFixed(4)}i`);

  return (
    <div className="parameter-explorer" data-testid="parameter-explorer" data-palette-theme={paletteTheme || 'amethyst'}>
      <div className="parameter-explorer-header">
        <span className="parameter-explorer-title">Parameter Plane (Mandelbrot c &rarr; Julia K<sub>c</sub>)</span>
        <span className="parameter-explorer-formula" data-testid="parameter-c-readout">
          c = {juliaC[0].toFixed(4)} {formatSign(juliaC[1])}
        </span>
      </div>
      <div className="parameter-explorer-presets" role="group" aria-label="Julia parameter presets">
        {PARAMETER_PRESETS.map(preset => {
          const active = !morphing && Math.abs(juliaC[0] - preset.c[0]) < 1e-4 && Math.abs(juliaC[1] - preset.c[1]) < 1e-4;
          return (
            <button
              key={preset.id}
              type="button"
              className={`parameter-preset-pill${active ? ' active' : ''}`}
              data-preset-id={preset.id}
              aria-pressed={active}
              onClick={() => {
                setMorphing(false);
                setJuliaC([...preset.c]);
              }}
            >
              {preset.label}
            </button>
          );
        })}
        <button
          type="button"
          className={`parameter-preset-pill parameter-preset-pill-morph${morphing ? ' active' : ''}`}
          data-testid="morph-parameter-button"
          aria-pressed={morphing}
          onClick={() => setMorphing(prev => !prev)}
        >
          {morphing ? 'Pause' : 'Morph'}
        </button>
        {onApplyParameter && (
          <button
            type="button"
            className="parameter-preset-pill parameter-preset-pill-action"
            data-testid="apply-parameter-to-canvas"
            onClick={() => onApplyParameter([...juliaC])}
          >
            Use Above
          </button>
        )}
      </div>
      <div className="parameter-explorer-grid">
        <div className="parameter-explorer-pane">
          <div
            className="parameter-canvas-stage"
            data-testid="mandelbrot-picker-stage"
            onMouseDown={event => {
              if (event.button !== 0) return;
              setDragging(true);
              updateFromClientPoint(event.clientX, event.clientY, event.currentTarget);
            }}
            onMouseMove={event => {
              if (!dragging) return;
              updateFromClientPoint(event.clientX, event.clientY, event.currentTarget);
            }}
            onMouseUp={() => setDragging(false)}
            onMouseLeave={() => setDragging(false)}
          >
            <canvas
              ref={mandelbrotCanvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              aria-label="Mandelbrot parameter plane picker"
            />
            <div
              className="parameter-crosshair"
              style={{
                left: `${Math.max(0, Math.min(100, markerLeft))}%`,
                top: `${Math.max(0, Math.min(100, markerTop))}%`,
              }}
              aria-hidden="true"
            />
          </div>
          <div className="parameter-pane-caption">Mandelbrot parameter plane (click or drag c)</div>
        </div>
        <div className="parameter-explorer-pane">
          <div className="parameter-canvas-stage">
            <canvas
              ref={juliaCanvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              aria-label="Live Julia set preview"
            />
          </div>
          <div className="parameter-pane-caption" data-testid="parameter-regime-status">
            {classification.label}
          </div>
        </div>
      </div>
    </div>
  );
}
